import styled from 'styled-components';
import { Briefcase, FileText, User, FilePlus } from 'react-feather';

// --- STYLED COMPONENTS ---
export const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: ${p => p.theme.colors.background.main};
`;

export const SidebarContainer = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 300px;
    background: #0d0d0d;
    color: #ffffff;
    padding: 40px 28px;
    border-right: 1px solid rgba(255, 255, 255, 0.07);
    position: relative;
    overflow: hidden;
    &::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 2px;
      background: linear-gradient(90deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.1) 100%);
    }
  }
`;

export const SidebarLogo = styled.img`
  width: 120px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  margin-bottom: 60px;
`;

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  position: relative;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: ${({ isActive }) => isActive ? 'rgba(255,255,255,0.06)' : 'transparent'};
  transition: background-color 0.2s;
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 27px;
    top: 52px;
    height: 24px;
    width: 2px;
    background: ${({ isCompleted }) => isCompleted ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.07)'};
    z-index: 1;
  }
`;

export const StepIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ isActive, isCompleted }) =>
    isActive ? '#ffffff' : isCompleted ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)'};
  color: ${({ isActive, isCompleted }) =>
    isActive ? '#0d0d0d' : isCompleted ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.22)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 2;
  font-weight: 700;
  font-size: 13px;
  box-shadow: ${({ isActive }) => isActive ? '0 0 0 3px rgba(255,255,255,0.12)' : 'none'};
  transition: all 0.2s;
`;

export const StepTextContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 4px;
`;

export const StepLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: ${({ isActive, isCompleted }) => (isActive || isCompleted) ? '600' : '400'};
  color: ${({ isActive, isCompleted }) =>
    isActive ? '#ffffff' : isCompleted ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)'};
  transition: color 0.2s;
`;

export const StepDesc = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: rgba(255,255,255,0.2);
  margin-top: 2px;
`;

export const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

export const MobileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  background-color: ${p => p.theme.colors.background.paper};
  border-bottom: 1px solid ${p => p.theme.colors.border.main};
  position: relative;
  z-index: 10;
  gap: 6px;
  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileHeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
  text-align: center;
`;

export const MobileProgressBar = styled.div`
  display: flex;
  width: 100%;
  height: 4px;
  background-color: ${p => p.theme.colors.border.main};
  @media (min-width: 1024px) {
    display: none;
  }
`;

export const MobileProgressFill = styled.div`
  height: 100%;
  background-color: ${p => p.theme.colors.text.primary};
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`;

export const FormArea = styled.div`
  flex: 1;
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  padding: 30px 20px 100px 20px;
  @media (min-width: 1024px) {
    padding: 60px 40px 120px 40px;
  }
`;

export const FormTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.theme.colors.text.primary};
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
`;

export const FormSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 32px 0;
`;

export const FormGroup = styled.div`
  margin-bottom: 24px;
`;

export const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
  @media (min-width: 768px) {
    flex-direction: row;
    > div {
      flex: 1;
      margin-bottom: 0;
    }
  }
`;

export const Label = styled.label`
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 8px;
`;

export const StyledInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.border.main)};
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  color: ${p => p.theme.colors.text.primary};
  background-color: ${p => p.theme.colors.background.paper};
  transition: all 0.2s;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.text.primary)};
    box-shadow: 0 0 0 3px ${({ $error, theme }) => ($error ? 'rgba(239, 68, 68, 0.1)' : theme.colors.background.selected)};
  }
  &::placeholder {
    color: ${p => p.theme.colors.text.disabled};
  }
`;

export const StyledSelect = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.border.main)};
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  color: ${p => p.theme.colors.text.primary};
  background-color: ${p => p.theme.colors.background.paper};
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${p => encodeURIComponent(p.theme.colors.text.primary)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.text.primary)};
    box-shadow: 0 0 0 3px ${({ $error, theme }) => ($error ? 'rgba(239, 68, 68, 0.1)' : theme.colors.background.selected)};
  }
`;

export const ErrorText = styled.span`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

export const UploadBox = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 30px;
  border: 2px dashed ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.border.main)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${p => p.theme.colors.background.paper};
  box-sizing: border-box;
  &:hover {
    background-color: ${p => p.theme.colors.background.main};
    border-color: ${p => p.theme.colors.text.disabled};
  }
  svg {
    color: ${p => p.theme.colors.text.secondary};
    margin-bottom: 12px;
  }
`;

export const UploadText = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.colors.text.primary};
  text-align: center;
`;

export const UploadSubtext = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${p => p.theme.colors.text.disabled};
  margin-top: 4px;
  text-align: center;
`;

export const FileUploadedBanner = styled.div`
  display: flex;
  align-items: center;
  background-color: ${p => p.theme.colors.background.selected === '#f3f4f6' ? '#f0fdf4' : 'rgba(34, 197, 94, 0.1)'};
  border: 1px solid #bbf7d0;
  padding: 12px 16px;
  border-radius: 10px;
  gap: 12px;
  svg {
    color: #22c55e;
  }
`;

export const FileName = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.colors.background.selected === '#f3f4f6' ? '#166534' : '#22c55e'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActionFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${p => p.theme.colors.background.paper};
  border-top: 1px solid ${p => p.theme.colors.border.main};
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
  @media (min-width: 1024px) {
    left: 320px;
    padding: 24px 60px;
  }
`;

export const GhostButton = styled.button`
  background: none;
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.secondary};
  cursor: pointer;
  padding: 12px 24px;
  border-radius: 10px;
  transition: background 0.2s;
  &:hover {
    background-color: ${p => p.theme.colors.background.main};
    color: ${p => p.theme.colors.text.primary};
  }
`;

export const SecondaryButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors.border.main};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.primary};
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background-color: ${p => p.theme.colors.background.paper};
    border-color: ${p => p.theme.colors.text.secondary};
  }
`;

export const PrimaryButton = styled.button`
  background-color: ${p => p.theme.colors.text.primary};
  color: ${p => p.theme.colors.background.paper};
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 14px 32px;
  border-radius: 10px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background-color: ${p => p.theme.colors.primary.light};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.colors.text.secondary)};
  cursor: pointer;
  line-height: 1.5;
  margin-top: 24px;
  input {
    margin-top: 3px;
    width: 16px;
    height: 16px;
    accent-color: ${p => p.theme.colors.text.primary};
  }
  span {
    flex: 1;
  }
`;

// --- DATA CONSTANTS ---
export const STEPS = [
  { id: 1, title: 'Entity Details', desc: 'Basic company information', icon: Briefcase },
  { id: 2, title: 'Operations', desc: 'Industry and volume', icon: FileText },
  { id: 3, title: 'Beneficial Owners', desc: 'Authorized representatives', icon: User },
  { id: 4, title: 'Documents', desc: 'Verification files', icon: FilePlus },
];

export const INDUSTRIES = [
  'E-commerce & Retail',
  'Software & Technology',
  'Financial Services',
  'Consulting & Professional Services',
  'Manufacturing',
  'Logistics & Transportation',
  'Import / Export',
  'Other'
];

export const VOLUMES = [
  'Less than $50,000',
  '$50,000 - $250,000',
  '$250,000 - $1,000,000',
  '$1,000,000 - $5,000,000',
  'More than $5,000,000'
];

export const SOURCE_OF_FUNDS = [
  'Business Revenue / Operations',
  'Investment Capital',
  'Loans / Credit Facility',
  'Asset Sales',
  'Other',
];

export const INTENDED_USE = [
  'Payroll',
  'Vendor Payments',
  'Cross-border Transfers',
  'Collections',
  'General Operations',
  'Other',
];

export const TRANSACTION_TYPES = [
  'ACH',
  'Wire Transfer',
  'SWIFT',
  'Check',
  'Multiple Types',
];

export const TRANSACTION_FREQUENCIES = [
  'Daily',
  'Weekly',
  'Monthly',
  'Occasionally',
];

export const AVERAGE_BALANCES = [
  'Less than $10,000',
  '$10,000 - $100,000',
  '$100,000 - $500,000',
  'More than $500,000',
];
